import { captureEvent } from './posthog'
import { PAYMENT_TYPES } from '@/api/receipts'

// The one custom event on the checkout path: `checkout_completed`, fired
// once per receipt that reaches a settled state (sale or refund).
//
// Why it exists: PostHog replay can only be searched by properties the app
// captured. A support ticket that quotes a receipt number is otherwise
// unsearchable -- the id is on screen and in the recorded /cart/commit
// response body, but neither is indexed. Shipping receipt_id/receipt_number
// as event properties turns "find the replay for receipt 77413" into a
// single filter (Event properties -> receipt_id -> equals; widen the date
// range, the default is Last 7 days).
//
// Store, register and build_version are super properties (posthog.ts
// syncIdentity) and land on this event automatically -- not repeated here.

// Machine-readable slugs, deliberately NOT the display labels in
// OrderDetailModal: these become PostHog property values that filters and
// saved insights are written against, so they have to stay stable even if
// the on-screen copy changes.
const PAYMENT_SLUGS: Record<number, string> = {
  [PAYMENT_TYPES.CASH]: 'cash',
  [PAYMENT_TYPES.EXCHANGE]: 'exchange',
  [PAYMENT_TYPES.MASTER_CARD]: 'mastercard',
  [PAYMENT_TYPES.POINTS]: 'points',
  [PAYMENT_TYPES.DEBIT]: 'debit',
  [PAYMENT_TYPES.VISA]: 'visa',
  [PAYMENT_TYPES.AMEX]: 'amex',
  [PAYMENT_TYPES.CHANGE]: 'change',
  [PAYMENT_TYPES.E_TRANSFER]: 'e_transfer',
  [PAYMENT_TYPES.AR]: 'on_account',
  [PAYMENT_TYPES.GIFT_CARD]: 'gift_card',
  [PAYMENT_TYPES.PAY_FROM_ACC]: 'account_balance',
  [PAYMENT_TYPES.CHEQUE]: 'cheque',
  [PAYMENT_TYPES.ROUNDING_DIFF]: 'rounding',
  [PAYMENT_TYPES.OTHER_CC]: 'other_card',
  [PAYMENT_TYPES.SPRINGBIG_POINTS]: 'springbig_points',
}

// Ledger artifacts the BE writes alongside a tender -- Change on a cash
// overpay, Rounding on a cash total. Counting them as tenders would make
// every overpaid cash sale look like a split payment.
const LEDGER_ARTIFACTS: ReadonlySet<number> = new Set([
  PAYMENT_TYPES.CHANGE,
  PAYMENT_TYPES.ROUNDING_DIFF,
])

// Deliberately loose: the four call sites hold the receipt as three
// different types (the modal's local ReceiptShape, api/receipts' Receipt,
// LastReceipt), all structurally compatible for the fields read here.
interface ReceiptLike {
  id?: number | string
  number?: string
  // `unknown` on purpose: api/receipts' ReceiptTotals is an interface, and
  // interfaces have no implicit index signature, so a `Record<string,
  // unknown>` field here would reject every real Receipt at the call site.
  totals?: unknown
  items?: unknown[]
  payments?: Array<{ payment_type?: number } | null | undefined> | null
}

/** Which tail settled the receipt -- lets a query separate the happy paths
 *  from the recovery ones without cross-referencing anything else. */
export type CheckoutPath =
  /** processPaymentFlow tail: cash, standalone card, gift card. */
  | 'tender'
  /** Moneris session came back approved (incl. post-failure reconcile). */
  | 'terminal'
  /** Commit/pay threw, re-read showed the receipt already Paid. */
  | 'reconciled'
  /** Loyalty covered the whole balance; no terminal session started. */
  | 'zero_charge'

interface CheckoutCompletedInput {
  receipt: ReceiptLike | null | undefined
  /** Fallback when the receipt snapshot carries no id of its own. */
  receiptId?: number | null
  isRefund: boolean
  path: CheckoutPath
  /** Customer ids are the replication key for reproducing a bug, and match
   *  what the network-capture scrubber already keeps. No name, no contact. */
  customerId?: number | null
}

function toNumber(value: unknown): number | undefined {
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

export function captureCheckoutCompleted({
  receipt,
  receiptId,
  isRefund,
  path,
  customerId,
}: CheckoutCompletedInput): void {
  const r = receipt ?? {}
  const totals = (r.totals ?? {}) as Record<string, unknown>

  const tenders = (Array.isArray(r.payments) ? r.payments : [])
    .map((p) => Number(p?.payment_type))
    .filter((t) => Number.isFinite(t) && !LEDGER_ARTIFACTS.has(t))
  // Unknown type numbers ship as `type_<n>` rather than dropping out, so a
  // new BE payment type shows up in the data instead of silently vanishing.
  const slugs = tenders.map((t) => PAYMENT_SLUGS[t] ?? `type_${t}`)

  const id = toNumber(r.id ?? receiptId)
  const discount = toNumber(totals.discount_amount ?? totals.discount)

  captureEvent('checkout_completed', {
    receipt_id: id ?? null,
    ...(r.number ? { receipt_number: r.number } : {}),
    total: toNumber(totals.total) ?? null,
    // The tender that closed the receipt; `payment_methods` carries the
    // whole split for the multi-tender case.
    payment_method: slugs.length > 0 ? slugs[slugs.length - 1] : null,
    payment_methods: [...new Set(slugs)],
    tender_count: slugs.length,
    is_refund: isRefund,
    completion_path: path,
    ...(Array.isArray(r.items) ? { item_count: r.items.length } : {}),
    ...(discount ? { discount_amount: discount } : {}),
    ...(customerId ? { customer_id: customerId } : {}),
  })
}
